<script setup lang="ts">
import type { PropType } from 'vue';

import {
    BackendErrorCode,
    type BackendErrorEntity,
    DirectSaleComputedState,
    DirectSaleState,
    type ErrorType,
    NegotiationState,
    type OrderEntity,
    useApiPlaceOrderDirectSale,
    type DirectSaleItemEntity,
} from '~/apiClient';
import type { DsdpBailModalType } from '~/components/Dsdp/DsdpBailModal.vue';
import { ROUTE_NAMES } from '~/constants/routes';
import { isDirectSaleItemEntityWithPrice, isDirectSaleWithNegotiations } from '~/types/directSale.type';
import { NotificationDuration } from '~/types/notifications.type';

// PROPS
const { directSale } = defineProps({
    directSale: { type: Object as PropType<DirectSaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const { notifyError, notifyGenericError } = useNotification();
const { logError } = useLogs();
const { data: authData, isLoggedIn } = useAuthUtils();
const { goToLoginPage } = useLoginRouteUtils();
const userGatedAction = useUserGatedActionState();
const { hasRestrictedNotice, hasInspectionNotice, isBailNeeded, hasOrder, isSold } = useDirectSaleStatus(toRef(() => directSale));
const { mutateAsync: mutatePlaceOrder, isPending: isOrderPending } = useApiPlaceOrderDirectSale();
const { makeProposal } = useDirectSaleNegotiation(() => directSale);
const route = useRoute();
const localePath = useLocalePath();
const { updateTrackingCompanyMetrics, trackDsNegotiationStartNegotiation, trackDsdpOrderModalClose } = useTracking();
const inspectionExpiredModalState = useModalState<boolean>();
const bailModalState = useModalState<boolean>();
const orderModalState = useModalState<boolean>();
const isLoadingModalOpen = ref(false);
const loadingModalTitle = ref('');

// DATA
const bailModalType = ref<DsdpBailModalType>('order');
const currentOrderId = ref<string | null>(null);
const currentNegotiationId = ref<string | null>(null);
const offerAmount = ref<number | undefined>(
    userGatedAction.value?.action === UserGatedAction.MAKE_OFFER ? userGatedAction.value.payload.offerAmount : directSale.price
);
// Track whether the user has modified the offer via the stepper, so we don't
// silently overwrite their chosen value if directSale.price changes at runtime.
const hasUserModifiedOffer = ref(userGatedAction.value?.action === UserGatedAction.MAKE_OFFER);
const isBuyer = computed(() => directSale.sellerCompanyId !== authData.value?.user?.companyId);
const makeOfferFlowInProgress = ref<boolean>(false);
const userMeta = computed(() => directSale.userMeta);
const shouldShowInspectionExpiredModal = computed(
    () =>
        !!userMeta.value?.inspectionExpiredExportRestrictions &&
        (isBailNeeded.value || (userMeta.value.isWhitelisted && !directSale?.negotiations?.length))
);

const hasMakeOffer = computed(() => {
    if (isLoggedIn.value && !isBuyer.value) return false;

    if (isDirectSaleWithNegotiations(directSale)) {
        const lastNegotiation = directSale.negotiations.toSorted((a, b) => {
            const dateA = Date.parse(a.createdAt);
            const dateB = Date.parse(b.createdAt);

            return dateB - dateA;
        })[0];
        if (lastNegotiation.state === NegotiationState.Open) return false;
    }

    return directSale.computedState === DirectSaleComputedState.NoActiveOrder;
});
const hasGoToNegotiation = computed(() => isLoggedIn.value && isDirectSaleWithNegotiations(directSale));
const showSection = computed(() => hasRestrictedNotice.value || hasInspectionNotice.value || hasMakeOffer.value || hasGoToNegotiation.value);

// HANDLERS
const placeOrderApi = (): Promise<OrderEntity> => {
    const apiPromise = mutatePlaceOrder(
        { data: { directSaleId: directSale.id } },
        {
            onError: (error: ErrorType<BackendErrorEntity>, variables: Object): void => {
                logError('Error placing DirectSale order', { apiError: error.data, apiData: variables });
            },
        }
    );

    apiPromise.catch((apiError: ErrorType<BackendErrorEntity>) => {
        if (!isBackendError(apiError.data)) {
            notifyGenericError();

            return;
        }

        const { error } = apiError.data;

        if ([BackendErrorCode.AuthTokenExpired, BackendErrorCode.AuthTokenNotValid].includes(error.code)) {
            // Errors already handled elsewhere
            return;
        }

        notifyError({
            title: tt(`sdp.DirectSale.order.orderError.title`),
            description: tt(`sdp.DirectSale.order.orderError.description`),
            duration: NotificationDuration.NoTimeout,
        });
    });

    return apiPromise;
};

// Resolves on success, throws on cancellation or error. The caller catches both
// uniformly to stop the loading state — no need to distinguish cancel from error.
const placeOrderSubFlow = async (): Promise<void> => {
    loadingModalTitle.value = tt('sdp.DirectSale.orderInProgressModal.title');

    if (isBailNeeded.value) {
        const order = await placeOrderApi();
        currentOrderId.value = order.id;
        if (!(await bailModalState.open())) throw undefined;
    } else {
        if (!(await orderModalState.open())) {
            trackDsdpOrderModalClose();
            throw undefined;
        }

        isLoadingModalOpen.value = true;
        await placeOrderApi();
    }

    isLoadingModalOpen.value = true;
    await until(isSold).toBe(true);
    updateTrackingCompanyMetrics();
};

// Resolves on success, throws on cancellation or error. The caller catches both
// uniformly to stop the loading state — no need to distinguish cancel from error.
const startNegotiationSubFlow = async (offerAmount: number): Promise<void> => {
    loadingModalTitle.value = tt('directSaleNegotiation.firstOfferInProgressModal.title');

    currentNegotiationId.value = await makeProposal(offerAmount);

    if (isBailNeeded.value) {
        if (!(await bailModalState.open())) throw undefined;
    }

    isLoadingModalOpen.value = true;
    await until(hasGoToNegotiation).toBe(true);
    navigateTo(localePath({ name: ROUTE_NAMES.DIRECT_SALE_DETAIL_NEGOTIATION, params: route.params }));
};

const makeOfferFlow = async (): Promise<void> => {
    if (offerAmount.value === undefined) return;

    makeOfferFlowInProgress.value = true;
    trackDsNegotiationStartNegotiation();

    try {
        if (!isLoggedIn.value) {
            userGatedAction.value = { action: UserGatedAction.MAKE_OFFER, payload: { offerAmount: offerAmount.value } };
            goToLoginPage();

            return;
        }

        if (hasInspectionNotice.value) {
            if (!(await inspectionExpiredModalState.open())) return;
        }

        const isPlaceOrderSubFlow = offerAmount.value >= directSale.buyNowPrice;
        bailModalType.value = isPlaceOrderSubFlow ? 'order' : 'negotiation';

        isPlaceOrderSubFlow ? await placeOrderSubFlow() : await startNegotiationSubFlow(offerAmount.value);
    } catch {
        // BSD-2821 Loader kept on success to avoid showing the page without loading while navigation is blocked by the destination page's suspense
        isLoadingModalOpen.value = false;
    } finally {
        makeOfferFlowInProgress.value = false;
    }
};

const closeModals = (): void => {
    inspectionExpiredModalState.cancel();
    bailModalState.cancel();
};

// LIFECYCLE
onMounted(() => {
    if (!isLoggedIn.value) return;

    if (userGatedAction.value?.action === UserGatedAction.MAKE_OFFER) {
        userGatedAction.value = null;
        makeOfferFlow();
    }
});

// WATCHERS
// Sync offerAmount with directSale.price only if the user hasn't touched the stepper.
watch(
    () => directSale.price,
    newPrice => {
        if (!hasUserModifiedOffer.value && newPrice !== undefined) {
            offerAmount.value = newPrice;
        }
    }
);

watch(isLoggedIn, () => {
    // Reset internal state on login change
    closeModals();
});

watch(
    () => directSale,
    value => {
        if (hasOrder.value || !bailModalState.isOpen) return;

        if (value.computedState === DirectSaleComputedState.PendingPayment) {
            closeModals();

            notifyError({
                title: tt('notifications.userDirectSaleAlreadyPurchased.title'),
                description: tt('notifications.userDirectSaleAlreadyPurchased.description', {
                    subject: tt('notifications.userDirectSaleAlreadyPurchased.inDirectSale'),
                }),
                duration: NotificationDuration.NoTimeout,
            });
        }

        if ([DirectSaleState.Closed, DirectSaleState.Canceled].includes(value.state) && makeOfferFlowInProgress.value) {
            closeModals();

            notifyError({
                title: tt(`sdp.DirectSale.order.expiredError.title`),
                description: tt(`sdp.DirectSale.order.expiredError.description`),
                duration: NotificationDuration.NoTimeout,
            });
        }
    }
);
</script>

<template>
    <div v-if="showSection" class="flex flex-col justify-center bg-white p-4 lg:p-6">
        <div class="flex flex-col gap-3">
            <SdpSalePanelNoticeRestricted v-if="hasRestrictedNotice" :sale="directSale" />
            <template v-else>
                <SdpSalePanelNoticeBase
                    v-if="hasInspectionNotice"
                    :message="tt(`sdp.notice.inspectionExpired`)"
                    icon-name="lucide:triangle-alert"
                    icon-class="text-warning-500"
                />

                <template v-if="hasMakeOffer">
                    <DsdpSalePanelMakeOffer
                        v-if="isDirectSaleItemEntityWithPrice(directSale)"
                        v-model="offerAmount"
                        :direct-sale="directSale"
                        @update:model-value="hasUserModifiedOffer = true"
                    />
                    <UButton
                        size="lg"
                        :ui="buttonResponsiveSizeUi('lg', 'xl')"
                        :label="tt('directSaleNegotiation.makeOfferButton')"
                        block
                        :loading="makeOfferFlowInProgress"
                        @click="makeOfferFlow"
                    />
                </template>

                <LazySdpInspectionExpiredModal
                    v-if="shouldShowInspectionExpiredModal"
                    v-model="inspectionExpiredModalState.isOpen"
                    @confirm="inspectionExpiredModalState.confirm(true)"
                />

                <LazyDsdpOrderModal
                    v-if="isDirectSaleItemEntityWithPrice(directSale)"
                    v-model="orderModalState.isOpen"
                    :direct-sale="directSale"
                    :loading="isOrderPending"
                    @confirm="orderModalState.confirm(true)"
                />

                <LazyDsdpBailModal
                    v-if="isBailNeeded && (currentOrderId || currentNegotiationId)"
                    v-model="bailModalState.isOpen"
                    :type="bailModalType"
                    :direct-sale="directSale"
                    :order-id="currentOrderId"
                    :negotiation-id="currentNegotiationId"
                    @success="bailModalState.confirm(true)"
                />
            </template>

            <SdpSalePanelGoToNegotiation v-if="hasGoToNegotiation" :sale="directSale" />
        </div>
    </div>
    <BaseLoadingModal v-model:open="isLoadingModalOpen" :title="loadingModalTitle" />
</template>
